
class WishlistDrawer extends HTMLElement {
  constructor() {
    super();
    this.items = JSON.parse(localStorage.getItem('wishlist_items')) || [];
    this.container = this.querySelector('[data-wishlist-items]');
    this.dialog = this.querySelector('dialog');
  }

  connectedCallback() {
    // Ensure only one instance exists
    const existing = document.querySelectorAll('wishlist-drawer');
    if (existing.length > 1 && existing[0] !== this) {
      this.remove();
      return;
    }

    // Move to body to prevent inheriting 'pointer-events: none' or 'visibility: hidden' from sticky headers
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return; // The browser will immediately call connectedCallback again when appended to the body
    }

    if (!this.initialized) {
      this.initialized = true;
      this.init();
    }
  }

  init() {
    this.updateUI();
    this.updateCardButtons();

    // Re-apply UI state if header is aggressively re-rendered by Shopify cart updates
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.addedNodes.length > 0) {
          const count = this.items.length;
          document.querySelectorAll('.header-actions__wishlist-count').forEach(el => {
            const span = el.querySelector('span');
            if (span) {
              if (span.textContent != count) span.textContent = count;
              span.hidden = false; // ensure inner span is visible
            } else if (el.textContent != count) {
              el.textContent = count;
            }
            if (el.hidden !== (count === 0)) el.hidden = (count === 0);
          });
          break; // Check once per batch
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Handle background click (overlay close) on dialog
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) {
        this.close();
      }
    });

    this.querySelector('[data-wishlist-close]')?.addEventListener('click', () => this.close());
  }

  addItem(item) {
    const exists = this.items.find(i => i.id === item.id);
    if (!exists) {
      this.items.push(item);
      localStorage.setItem('wishlist_items', JSON.stringify(this.items));
      this.updateUI();
      this.updateCardButtons();
      this.showToast(item.title + ' added to Favourites');
    }
  }

  showToast(message, linkHtml = '') {
    let container = document.getElementById('cc-toast-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'cc-toast-container';
      document.body.appendChild(container);
    }

    const toast = document.createElement('div');
    toast.className = 'cc-toast';
    toast.innerHTML = `
      <span class="cc-toast__icon">
        <svg viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 5L4.5 8.5L11 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
       <div class="cc-toast__content">
        <span class="cc-toast__message">${message}</span>
        ${linkHtml}
      </div>
    `;

    container.appendChild(toast);

    // Trigger animation
    setTimeout(() => toast.classList.add('cc-toast--show'), 10);

    // Remove after delay
    setTimeout(() => {
      toast.classList.remove('cc-toast--show');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  removeItem(id) {
    this.items = this.items.filter(i => i.id !== id);
    localStorage.setItem('wishlist_items', JSON.stringify(this.items));
    this.updateUI();
    this.updateCardButtons();
  }

  updateCardButtons() {
    document.querySelectorAll('[data-wishlist-add]').forEach(btn => {
      const card = btn.closest('product-card');
      let id;
      if (card) {
        id = card.querySelector('input[name="id"]')?.value || card.dataset.productId;
      } else if (btn.dataset.itemData) {
        try {
          id = JSON.parse(btn.dataset.itemData).id;
        } catch(e) {}
      }

      if (id && this.items.find(i => i.id == id)) {
        btn.classList.add('is-active');
        btn.style.color = '#e74c3c';
        const svg = btn.querySelector('svg');
        if (svg) {
          svg.style.fill = '#e74c3c';
          svg.style.stroke = '#e74c3c';
        }
      } else {
        btn.classList.remove('is-active');
        btn.style.color = '';
        const svg = btn.querySelector('svg');
        if (svg) {
          svg.style.fill = 'none';
          svg.style.stroke = 'currentColor';
        }
      }
    });
  }

  updateUI() {
    // Update count bubbles
    const count = this.items.length;
    document.querySelectorAll('.header-actions__wishlist-count').forEach(el => {
      const span = el.querySelector('span');
      if (span) {
        span.textContent = count;
        if (count === 0) {
          span.setAttribute('hidden', '');
        } else {
          span.removeAttribute('hidden');
        }
      } else {
        el.textContent = count;
      }
      
      el.hidden = count === 0;
    });

    if (!this.container) return;

    if (count === 0) {
      this.container.innerHTML = '<div class="wishlist-empty">Your wishlist is empty</div>';
      return;
    }

    this.container.innerHTML = this.items.map(item => `
      <div class="wishlist-item" data-id="${item.id}">
        <a href="${item.url}" class="wishlist-item__image">
          <img src="${item.image}" alt="${item.title}" width="80" height="100">
        </a>
        <div class="wishlist-item__info">
          <a href="${item.url}" class="wishlist-item__title">${item.title}</a>
          ${item.variant_title ? `<p class="wishlist-item__variant">${item.variant_title}</p>` : ''}
          <div class="wishlist-item__price">${item.price}</div>
          <div class="wishlist-item__actions">
             <button class="button button--secondary button--small wishlist-item__atc" onclick="window.wishlistAddItemToCart('${item.id}', this)">Add to Cart</button>
             <button class="wishlist-item__remove" onclick="document.querySelector('wishlist-drawer').removeItem('${item.id}')" aria-label="Remove item" title="Remove">
               <span class="wishlist-item__remove-icon" style="--icon-stroke-width: 1.5px; display: inline-flex;">
                 <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                   <path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"/>
                   <path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
                 </svg>
               </span>
             </button>
          </div>
        </div>
      </div>
    `).join('');
  }

  open() {
    if (!this.dialog.hasAttribute('open')) {
       this.dialog?.showModal();
    }
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (this.dialog.hasAttribute('open')) {
       this.dialog?.close();
    }
    this.classList.remove('is-open');
    document.body.style.overflow = '';
  }
}

customElements.define('wishlist-drawer', WishlistDrawer);

// Global Event Listeners for Wishlist Actions
document.addEventListener('click', (e) => {
  const addBtn = e.target.closest('[data-wishlist-add]');
  if (addBtn) {
    const drawer = document.querySelector('wishlist-drawer');
    if (!drawer) return;

    let itemData;
    const card = addBtn.closest('product-card');
    
    if (card) {
      const variantId = card.querySelector('input[name="id"]')?.value || card.dataset.productId;
      const title = card.querySelector('.cc-card__vendor')?.textContent || '';
      const variantTitle = card.querySelector('.cc-card__var-title')?.textContent || '';
      const priceVal = card.querySelector('.cc-price-num')?.textContent || '';
      const currency = card.querySelector('.cc-price-currency')?.textContent || '';
      const img = card.querySelector('.cc-card__img-main')?.src || '';
      const url = card.querySelector('a')?.href || '';
      
      itemData = {
        id: variantId,
        title: title,
        variant_title: variantTitle,
        price: currency + priceVal,
        image: img,
        url: url
      };
    } else if (addBtn.dataset.itemData) {
      try {
         itemData = JSON.parse(addBtn.dataset.itemData);
      } catch(err) {}
    }

    if (itemData && itemData.id) {
      if (drawer.items.find(i => i.id == itemData.id)) {
        drawer.removeItem(itemData.id);
      } else {
        drawer.addItem(itemData);
      }
    }
  }

  const toggleBtn = e.target.closest('[data-wishlist-toggle]');
  if (toggleBtn) {
    const drawer = document.querySelector('wishlist-drawer');
    if (drawer) drawer.open();
  }
});

// Helper for Add to Cart from wishlist
window.wishlistAddItemToCart = async (id, btn) => {
  btn.classList.add('is-loading');
  try {
    const formData = new FormData();
    formData.append('id', id);
    formData.append('quantity', 1);
    
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sections = Array.from(cartItemsComponents).map(c => c.dataset.sectionId).filter(Boolean);
    if (sections.length > 0) formData.append('sections', sections.join(','));

    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      body: formData
    });
    
    if (response.ok) {
       const data = await response.json();
       // Trigger cart update
       document.dispatchEvent(new CustomEvent('cart:update', {
          detail: { data: data }
       }));
       // Close wishlist and open cart
       document.querySelector('wishlist-drawer').close();
       document.querySelector('cart-drawer-component')?.open();
    }
  } catch (err) {
    console.error(err);
  } finally {
    btn.classList.remove('is-loading');
  }
};
